import { useState } from "react";
import { Youtube, Tv, Radio } from "lucide-react";

const VIDEO_SOURCES = [
  { id: "youtube", label: "YouTube", icon: Youtube, color: "bg-red-600" },
  { id: "vcloud", label: "VCloud/Hudl", icon: Tv, color: "bg-orange-600" },
  { id: "m3u8", label: "HLS Stream", icon: Radio, color: "bg-blue-600" },
];

export const YouTubeStreamSection = ({ formData, setFormData }) => {
  const [activeTab, setActiveTab] = useState(
    formData.m3u8_url ? 'm3u8' : formData.vcloud_url ? 'vcloud' : 'youtube'
  );

  const activeSource = formData.active_video_source || "auto";

  // Count how many sources have a URL set
  const sourcesWithUrl = [
    formData.youtube_url && "youtube",
    formData.vcloud_url && "vcloud",
    formData.m3u8_url && "m3u8",
  ].filter(Boolean);

  return (
    <div className="p-4 bg-zinc-800/50 border border-white/5">
      {/* Video Source Tabs */}
      <div className="flex items-center gap-1 mb-4 overflow-x-auto scrollbar-hide">
        {VIDEO_SOURCES.map(src => (
          <button
            key={src.id}
            type="button"
            onClick={() => setActiveTab(src.id)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
              activeTab === src.id
                ? `${src.color} text-white`
                : 'bg-zinc-700 text-zinc-400 hover:text-white'
            }`}
            data-testid={`video-source-tab-${src.id}`}
          >
            <src.icon className="w-4 h-4" />
            {src.label}
          </button>
        ))}
      </div>

      {/* YouTube Input */}
      {activeTab === 'youtube' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Youtube className="w-4 h-4 text-red-500" />
            <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">YouTube Stream URL</h4>
          </div>
          <input 
            type="text" 
            value={formData.youtube_url || ''} 
            onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })} 
            placeholder="https://www.youtube.com/watch?v=... or https://youtube.com/live/..."
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="youtube-url-input"
          />
          <p className="text-xs text-zinc-500 mt-2">
            Use YouTube for stream? Enter URL above. No channel needed if using YouTube.
          </p>
        </div>
      )}

      {/* VCloud/Hudl Input */}
      {activeTab === 'vcloud' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Tv className="w-4 h-4 text-orange-500" />
            <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">VCloud/Hudl Embed URL</h4>
          </div>
          <input 
            type="text" 
            value={formData.vcloud_url || ''} 
            onChange={(e) => setFormData({ ...formData, vcloud_url: e.target.value })} 
            placeholder="https://vcloud.hudl.com/broadcast/embed/..."
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="vcloud-url-input"
          />
          <p className="text-xs text-zinc-500 mt-2">
            Paste the VCloud embed URL from Hudl. Must be an embed URL (contains /embed/ in the path).
          </p>
          {formData.vcloud_url && !formData.vcloud_url.includes('/embed/') && (
            <p className="text-xs text-yellow-500 mt-1">
              Warning: URL should contain "/embed/" — regular Hudl Fan links won't work for embedding.
            </p>
          )}
        </div>
      )}

      {/* HLS/m3u8 Input */}
      {activeTab === 'm3u8' && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Radio className="w-4 h-4 text-blue-500" />
            <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">HLS Stream URL</h4>
          </div>
          <input 
            type="text" 
            value={formData.m3u8_url || ''} 
            onChange={(e) => setFormData({ ...formData, m3u8_url: e.target.value })} 
            placeholder="https://stream.example.com/live/stream.m3u8"
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="m3u8-url-input"
          />
          <p className="text-xs text-zinc-500 mt-2">
            Paste an m3u8 HLS stream URL for direct live playback. Works with most CDN and OTT providers.
          </p>
        </div>
      )}

      {/* Active Source Selector — only show when more than one source has a URL */}
      {sourcesWithUrl.length > 1 && (
        <div className="mt-4 pt-4 border-t border-white/10">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
            Active Video Source
          </label>
          <select
            value={activeSource}
            onChange={(e) => setFormData({ ...formData, active_video_source: e.target.value })}
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="active-video-source-select"
          >
            <option value="auto">Auto (first available)</option>
            {formData.youtube_url && <option value="youtube">YouTube</option>}
            {formData.vcloud_url && <option value="vcloud">VCloud/Hudl</option>}
            {formData.m3u8_url && <option value="m3u8">HLS Stream (m3u8)</option>}
          </select>
          <p className="text-xs text-zinc-600 mt-1">Choose which video source to display on the game page</p>
        </div>
      )}
    </div>
  );
};
